<template>
    <div ref="filterSelectContainer">
        <pc-label
            v-if="label"
            v-bind:bold="boldLabel"
            v-bind:label="label"
            v-bind:alt-label="altLabel"
            v-bind:show-required="required"
            v-bind:show-help="showHelp"
            v-on:help-clicked="$emit('help-clicked')" />
        <div
            ref="filteredSelectFieldDisplay"
            class="position-relative wrapper"
            v-bind:aria-invalid="hasValidationError"
            v-bind:aria-describedby="validationErrorId"
            tabindex="0"
            v-on:keyup.enter.stop="handleKeyPress"
            v-on:keyup.space="handleKeyPress">
            <div
                v-bind:class="selectedViewClass"
                data-test-role="filteredSelectField"
                v-bind:readonly="readonly"
                v-bind:disabled="disabled"
                v-bind:style="selectionStyleObject"
                v-on:click="toggleOptions"
                v-on:keydown.space="toggleOptions">
                <div class="d-flex align-items-center justify-content-between">
                    <div
                        class="selected-display overflow-hidden pe-2 text-nowrap text-truncate"
                        data-test-role="selected-item-text">
                        {{ selectedItemText }}
                    </div>
                    <pc-bs-icon
                        name="chevron-down"
                        style="min-width: 1rem" />
                </div>
            </div>
            <pc-transition
                transition="slide"
                fast
                v-on:before-enter="setBorderFalse"
                v-on:enter="setBorderFalse"
                v-on:after-leave="setBorderTrue">
                <div
                    v-if="showOptions"
                    ref="filterList"
                    class="options-wrapper p-2 round bg-white w-100"
                    v-bind:style="listOptionsStyleObject"
                    v-on:keydown.up="handleKeyPress"
                    v-on:keydown.down="handleKeyPress">
                    <div class="d-flex flex-column h-100">
                        <div class="row g-0">
                            <basic-search
                                ref="listSearch"
                                v-bind:dynamic="{ debounce: 0, reactive: true }"
                                class="col"
                                v-bind:aria-controls="filteredSelectOptionsListId"
                                v-bind:aria-active-descendant-id="activeDescendantId"
                                v-bind:aria-expanded="showOptions"
                                v-bind:role="'combobox'"
                                v-bind:external-native-handlers="{ keydown: disableDefaultBehavior }"
                                v-on:search="updateSearchTerm" />
                        </div>
                        <div
                            class="overflow-auto flex-grow-1"
                            v-bind:style="listWrapperStyleObject">
                            <filtered-select-options
                                ref="filteredSelectOptionsRef"
                                v-bind:filtered-items="filteredList"
                                v-bind:search-term="currentSearchTerm"
                                v-bind:selected-model-value="modelValue"
                                v-bind:value-prop="valueProp"
                                v-bind:text-prop="textProp"
                                v-bind:list-id="filteredSelectOptionsListId"
                                v-on:on-select="onSelect"
                                v-on:set-active-descendant-id="setActiveDescendantId">
                                <template v-slot:optionDisplay="itemWrapper">
                                    <slot v-bind="itemWrapper">
                                        <span class="text-wrap-and-truncate">
                                            <template
                                                v-for="highlightedSubstringOption in itemWrapper.highlighted"
                                                v-bind:key="highlightedSubstringOption.SubIndexOrder">
                                                <mark
                                                    v-if="highlightedSubstringOption.ShowHighlight"
                                                    class="mark-format">
                                                    {{ highlightedSubstringOption.DisplayText }}
                                                </mark>
                                                <template v-else>
                                                    {{ highlightedSubstringOption.DisplayText }}
                                                </template>
                                            </template>
                                        </span>
                                    </slot>
                                </template>
                            </filtered-select-options>
                        </div>
                    </div>
                </div>
            </pc-transition>
        </div>
        <div
            v-if="showValidationErrors"
            v-bind:id="validationErrorId"
            aria-live
            class="invalid-feedback fw-bold">
            {{ validationError }}
        </div>
    </div>
</template>

<script setup>
    import { computed, ref, watch, onMounted, onUnmounted, nextTick, useTemplateRef } from "vue";
    import filteredSelectOptions from "./filteredSelectOptions.vue";
    import basicSearch from "Components/search/basicSearch.vue";
    import { useFilterableList } from "Components/filterableList/filterableList";
    import { useResource } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./pc-filtered-select.i18n";
    import { simpleDebounce } from "Util/util";

    const props = defineProps({
        modelValue: { type: [String, Number], required: false, default: null },
        label: { type: String, required: false, default: null },
        altLabel: { type: String, required: false, default: null },
        boldLabel: { type: Boolean, required: false, default: false },
        items: { type: Array, required: true },
        valueProp: { type: String, required: false, default: "value" },
        textProp: { type: String, required: false, default: "text" },
        validator: { type: Object, required: false, default: null },
        validationEnabled: { type: Boolean, required: false, default: true },
        externalValidationResult: { type: Object, required: false, default: null },
        showValidationErrors: { type: Boolean, required: false, default: true },
        required: { type: Boolean, required: false, default: false },
        showHelp: { type: Boolean, required: false, default: false },
        readonly: { type: Boolean, required: false, default: false },
        disabled: { type: Boolean, required: false, default: false },
        placeholderText: { type: String, required: false, default: null },
        formatForModal: { type: Boolean, required: false, default: false }
    });

    const EVENT_ARROW_UP = "ArrowUp";
    const EVENT_ARROW_DOWN = "ArrowDown";
    const EVENT_ENTER = "Enter";
    const EVENT_SPACE = "Space";

    const emit = defineEmits(["update:modelValue", "setActiveDescendantId", "validated", "help-clicked"]);

    // template refs
    const listSearch = useTemplateRef("listSearch");
    const filteredSelectOptionsRef = useTemplateRef("filteredSelectOptionsRef");
    const filterSelectContainer = useTemplateRef("filterSelectContainer");
    const filteredSelectFieldDisplay = ref(null);

    // validation
    const validationResult = ref(null);

    const validationError = computed(() => {
        if (props.externalValidationResult) {
            return props.externalValidationResult.error;
        }
        if (validationResult.value) {
            return validationResult.value.error;
        }
        return null;
    });
    const hasValidationError = computed(() => {
        return Boolean(validationError.value);
    });
    const hasValidator = computed(() => {
        return props.validator !== null && props.validator !== undefined;
    });
    const validationErrorId = (props.label ? props.label.trim() : props.altLabel.trim()) + "-validation-error";

    // dropdown handling
    const showOptions = ref(false);
    const showSelectionBottomBorder = ref(true);

    const listWrapperStyleObject = computed(() => {
        return {
            maxHeight: "300px"
        };
    });
    const selectionStyleObject = computed(() => {
        if (showSelectionBottomBorder.value) {
            return null;
        }
        return {
            borderBottom: "none",
            borderBottomRightRadius: "0",
            borderBottomLeftRadius: "0"
        };
    });
    const listOptionsStyleObject = computed(() => {
        return {
            position: props.formatForModal ? "relative" : "absolute"
        };
    });
    const filteredSelectOptionsListId =
        (props.label ? props.label.replace(/\s+/g, "") : props.altLabel.replace(/\s+/g, "")) + "optionsListId";

    // i18n
    const { selectElementPromptText } = useResource(KEYS, NAMESPACE);

    // item selection handling
    const currentSearchTerm = ref("");
    const activeDescendantId = ref(null);

    const referenceItems = computed(() => {
        return props.items;
    });
    const { filteredList } = useFilterableList(referenceItems, currentSearchTerm, filter, props.searchTermParser);

    const selectedItem = computed(() => {
        // use == instead of === to account for modelValues that are not strings
        return props.items.find((item) => item[props.valueProp] == props.modelValue);
    });
    const selectedItemText = computed(() => {
        return selectedItem.value?.[props.textProp] || promptText.value;
    });
    const promptText = computed(() => {
        return props.placeholderText || selectElementPromptText.value;
    });
    const selectedViewClass = computed(() => {
        return {
            "is-invalid": hasValidationError.value,
            "form-control selection-control": true
        };
    });
    // if either readonly or disabled is true, component should not be interactive
    const disableInteraction = computed(() => {
        return props.readonly || props.disabled;
    });

    // keyboard control computeds
    const handleKeyPress = (event) => {
        if (event.code === EVENT_ARROW_UP) {
            filteredSelectOptionsRef.value.moveFocusUp();
        } else if (event.code === EVENT_ARROW_DOWN) {
            filteredSelectOptionsRef.value.moveFocusDown();
        } else if (event.code == EVENT_ENTER) {
            if (showOptions.value) {
                filteredSelectOptionsRef.value.onOptionSelected();
            } else {
                toggleOptions();
            }
        } else if (event.code == EVENT_SPACE && !showOptions.value) {
            toggleOptions();
        }
    };
    // used to overwrite existing input component behavior
    const disableDefaultBehavior = (event) => {
        if (event.code === EVENT_ARROW_DOWN || event.code === EVENT_ARROW_UP) {
            event.preventDefault();
        }
    };

    onMounted(() => {
        document.addEventListener("click", onGlobalClick);
        // closes dropdown if page is resized
        window.addEventListener("resize", simpleDebounce(onGlobalClick));
    });
    onUnmounted(() => {
        document.removeEventListener("click", onGlobalClick);
        window.removeEventListener("resize", simpleDebounce(onGlobalClick));
    });

    watch(showOptions, () => {
        // attempt validation when dropdown is closed, resets currentSearchTerm
        if (!showOptions.value) {
            _validate(props.modelValue);
            updateSearchTerm("");
        }
    });

    //methods
    function _validate(value) {
        if (props.validationEnabled && hasValidator.value) {
            validationResult.value = props.validator.validate(value);
            emit("validated", validationResult.value);
        }
    }

    function filter(item, searchTerm) {
        return searchTerm.test(item[props.textProp]);
    }
    function onSelect(item) {
        if (item != null) {
            emit("update:modelValue", item[props.valueProp]);
            showOptions.value = false;
            // maintain focus on component after selection has been made
            filteredSelectFieldDisplay.value?.focus();
        } else {
            clearSelection();
        }
    }

    function clearSelection() {
        emit("update:modelValue", null);
        // focus on search
        focusIn();
    }

    function setActiveDescendantId(focusIndex) {
        activeDescendantId.value = focusIndex;
    }

    function updateSearchTerm(searchTerm) {
        currentSearchTerm.value = searchTerm;
    }

    function focusIn() {
        listSearch.value?.focusIn();
    }

    async function toggleOptions() {
        if (!disableInteraction.value) {
            showOptions.value = !showOptions.value;
            if (showOptions.value) {
                await nextTick();
                focusIn();
            }
        }
    }
    function setBorderFalse() {
        showSelectionBottomBorder.value = false;
    }
    function setBorderTrue() {
        showSelectionBottomBorder.value = true;
    }

    function onGlobalClick(event) {
        if (!event.composedPath().includes(filterSelectContainer.value)) {
            showOptions.value = false;
        }
    }
</script>

<style lang="scss" scoped>
    .wrapper:focus {
        outline: 0;
        .selection-control {
            color: var(--pc-input-focus-color);
            background-color: var(--pc-input-focus-bg);
            border-color: var(--pc-input-focus-border-color);
            box-shadow: var(--pc-input-focus-box-shadow);
        }
    }

    .selected-display {
        height: calc(var(--pc-input-line-height) * 1em);
    }
    .options-wrapper {
        border: 1px solid var(--pc-input-border-color);
        border-top: none;
        border-bottom-right-radius: var(--pc-input-border-radius);
        border-bottom-left-radius: var(--pc-input-border-radius);
        // options should be visible above other DOM elements when dropdown
        // is displayed
        z-index: 100;
    }

    .text-wrap-and-truncate {
        display: -webkit-box;
        -webkit-box-orient: vertical;
        overflow: hidden;
        -webkit-line-clamp: 2; // Clamp at 2 lines (show up to 2, truncate 3rd+)
        line-clamp: 2;
        white-space: pre-line;
        text-overflow: ellipsis;
        word-break: break-word;
        overflow-wrap: break-word;
    }

    // potentially move to program wide file
    .mark-format {
        font-weight: bold;
        background-color: transparent;
        margin: 0px;
        padding: 0px;
    }
</style>
